import React, { useEffect, useState } from 'react';
import {
  IPackage,
  ITaxonomy,
  ICardInformation,
  VehicleSelectorType,
  ICardInformationBasic,
  ICardInformationComplex,
  IRequestAQuoteData,
  IRAQConfiguration,
} from 'interfaces/requestAQuote';
import { useStyles } from './VehicleSelector.styles';
import { FormControl, Grid } from '@mui/material';
import { VehicleCard } from 'components/RequestAQuote/VehicleCard/VehicleCard';
import { trackEvent } from 'helpers/Analytics';
import { useDispatch } from 'react-redux';
import { setVehicleData } from 'redux/slices/vehicleSlice';
import TextRender from 'components/Global/TextRender/TextRender';
import { parseColorData } from 'helpers/Color';
import { LinkButton } from 'components/Global/LinkButton/LinkButton';
import {
  setConfigStatus,
  setCurrentConfig,
} from 'redux/slices/savedConfigSlice';
import Dropdown, {
  createDropdownItemObj,
} from 'components/Global/Dropdown/Dropdown';
import { createIdFromText, cleanHTMLFromString } from 'helpers/FormatText';

export interface IVehicleSelector {
  data: IRequestAQuoteData;
  configuration?: IRAQConfiguration;
  onEnabled?: Function;
  showModelError: boolean;
  showPackageError: boolean;
}

const getTransitLotText = (
  numberInTransit: number,
  numberOnSite: number,
  labels: { [key: string]: string }
) => {
  const result = [];
  numberInTransit > 0 && result.push(`${numberInTransit} ${labels.inTransit}`);
  numberOnSite > 0 && result.push(`${numberOnSite} ${labels.onLot}`);
  return `${result.join(' / ')}${
    result.length > 0 ? labels.inventoryDisclaimer : ''
  }`;
};

const findSelectedItemsByModelId = (
  taxonomies: ITaxonomy[],
  modelId?: string
): {
  newSelectedTaxonomy?: ITaxonomy;
  newSelectedModel?: ITaxonomy;
  newSelectedPackage?: IPackage;
} => {
  const result: {
    newSelectedTaxonomy?: ITaxonomy;
    newSelectedModel?: ITaxonomy;
    newSelectedPackage?: IPackage;
  } = {};

  for (const taxonomy of taxonomies) {
    for (const vehicle of taxonomy.vehicles) {
      const selectedPackage = vehicle.packages?.find(
        (_package) => _package.vehicleModel === modelId
      );
      if (selectedPackage) {
        result.newSelectedTaxonomy = taxonomy;
        result.newSelectedModel = vehicle;
        result.newSelectedPackage = selectedPackage;
        return result;
      }
    }
  }
  return result;
};

const findSelectedItemsByYearAndSeries = (
  taxonomies: ITaxonomy[],
  modelSeries: string,
  modelYear: string
): { newSelectedTaxonomy?: ITaxonomy; newSelectedModel?: ITaxonomy } => {
  const result: {
    newSelectedTaxonomy?: ITaxonomy;
    newSelectedModel?: ITaxonomy;
  } = {};
  for (const taxonomy of taxonomies) {
    const selectedVehicle = taxonomy?.vehicles.find(
      (vehicle) =>
        vehicle.taxonomyVehicle === modelSeries &&
        vehicle.modelYear === modelYear
    );

    if (selectedVehicle) {
      result.newSelectedTaxonomy = taxonomy;
      result.newSelectedModel = selectedVehicle;
      return result;
    }
  }
  return result;
};

const findSelectedItemsBySeries = (
  taxonomies: ITaxonomy[],
  modelSeries: string
): { newSelectedTaxonomy?: ITaxonomy; newSelectedModel?: ITaxonomy } => {
  const result: {
    newSelectedTaxonomy?: ITaxonomy;
    newSelectedModel?: ITaxonomy;
  } = {};
  for (const taxonomy of taxonomies) {
    const selectedVehicle = taxonomy?.vehicles.find(
      (vehicle) => vehicle.taxonomyVehicle === modelSeries
    );

    if (selectedVehicle) {
      result.newSelectedTaxonomy = taxonomy;
      result.newSelectedModel = selectedVehicle;
      return result;
    }
  }
  return result;
};

export function VehicleSelector({
  data,
  configuration,
  onEnabled,
  showModelError,
  showPackageError,
}: IVehicleSelector) {
  const dispatch = useDispatch();
  const classes = useStyles();
  const { pageContent, taxonomy } = data;
  const { labels } = pageContent;
  const taxonomies = taxonomy as ITaxonomy[];
  const [selectedTaxonomy, setSelectedTaxonomy] = useState(taxonomies?.[0]);
  const [selectedModel, setSelectedModel] = useState<ITaxonomy>();
  const [selectedPackage, setSelectedPackage] = useState<
    IPackage | undefined
  >();
  const [vehicleCardInfo, setVehicleCardInfo] = useState<
    ICardInformation | undefined
  >();
  const isFormDisabled =
    !!(
      configuration?.modelId &&
      configuration.variant !== VehicleSelectorType.savedConfig
    ) || configuration?.disableForm;

  let variant = configuration?.variant;

  // adding this condition for those offers which don't apply for an specific model (special programs)
  if (variant === VehicleSelectorType.offers && !configuration?.modelId) {
    variant = VehicleSelectorType.basic;
  }

  const handleTaxonomyClick = (taxonomy: ITaxonomy) => {
    !isFormDisabled && setSelectedTaxonomy(taxonomy);
    trackEvent({
      label: `selector:${taxonomy.taxonomyName}`,
      category: {
        includePageName: true,
        text: 'request a quote selector',
      },
      type: 'selector',
      modelSeries: selectedModel?.taxonomyVehicle || 'no model',
      modelYear: selectedModel?.modelYear || 'no model',
    });
  };
  useEffect(() => {
    if (configuration && configuration.modelId) {
      const { newSelectedTaxonomy, newSelectedModel, newSelectedPackage } =
        findSelectedItemsByModelId(taxonomies, configuration.modelId);

      newSelectedTaxonomy && setSelectedTaxonomy(newSelectedTaxonomy);
      newSelectedModel && setSelectedModel(newSelectedModel);
      newSelectedPackage && setSelectedPackage(newSelectedPackage);
      dispatch(
        setVehicleData({
          modelSeries: newSelectedTaxonomy?.taxonomyVehicle || '',
          modelYear: newSelectedModel?.modelYear || '',
          modelID: newSelectedPackage?.vehicleModel || '',
        })
      );
    } else if (
      configuration &&
      configuration.modelSeries &&
      configuration.modelYear
    ) {
      const { newSelectedTaxonomy, newSelectedModel } =
        findSelectedItemsByYearAndSeries(
          taxonomies,
          configuration.modelSeries,
          configuration.modelYear
        );

      newSelectedTaxonomy && setSelectedTaxonomy(newSelectedTaxonomy);
      newSelectedModel && setSelectedModel(newSelectedModel);
      dispatch(
        setVehicleData({
          modelSeries: selectedModel?.taxonomyVehicle || '',
          modelYear: selectedModel?.modelYear || '',
          modelID: undefined,
        })
      );
    } else if (configuration && configuration.modelSeries) {
      const { newSelectedTaxonomy, newSelectedModel } =
        findSelectedItemsBySeries(taxonomies, configuration.modelSeries);

      newSelectedTaxonomy && setSelectedTaxonomy(newSelectedTaxonomy);
      newSelectedModel && setSelectedModel(newSelectedModel);
      dispatch(
        setVehicleData({
          modelSeries: selectedModel?.taxonomyVehicle || '',
          modelYear: undefined,
          modelID: undefined,
        })
      );
    } else {
      dispatch(
        setVehicleData({
          modelSeries: undefined,
          modelYear: undefined,
          modelID: undefined,
        })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const { newSelectedTaxonomy, newSelectedModel, newSelectedPackage } =
      findSelectedItemsByModelId(taxonomies, configuration?.modelId);

    newSelectedTaxonomy && setSelectedTaxonomy(newSelectedTaxonomy);
    newSelectedModel && setSelectedModel(newSelectedModel);
    newSelectedPackage && setSelectedPackage(newSelectedPackage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configuration]);

  useEffect(() => {
    if (selectedModel && !isFormDisabled) {
      const newVehicleCardInfo: ICardInformationBasic = {
        imgAlt: selectedModel?.defaultVehicleImageAlt,
        img: selectedModel?.defaultVehicleImage,
        modelName: selectedModel.taxonomyVehicle as string,
        modelYear: selectedModel.modelYear as string,
      };
      if (newVehicleCardInfo !== vehicleCardInfo) {
        setVehicleCardInfo(newVehicleCardInfo);
      }
      if (configuration?.variant !== VehicleSelectorType.savedConfig) {
        setSelectedPackage(undefined);
      }
      dispatch(
        setVehicleData({
          modelSeries: selectedModel?.taxonomyVehicle || '',
          modelYear: selectedModel?.modelYear || '',
          modelID: undefined,
        })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedModel]);

  useEffect(() => {
    const vehicleInfo = data.vehicleData;
    if (selectedPackage) {
      let newVehicleCardInfo: ICardInformation = {
        modelName: selectedModel?.taxonomyVehicle as string,
        modelYear: selectedModel?.modelYear as string,
      };
      if (!configuration || variant === VehicleSelectorType.basic) {
        newVehicleCardInfo = {
          ...newVehicleCardInfo,
          imgAlt: selectedPackage?.packageImageAlt,
          img: selectedPackage?.packageImageUrl,
          description: selectedPackage.packageDescription,
        } as ICardInformationBasic;
      } else if (
        variant === VehicleSelectorType.bap ||
        variant === VehicleSelectorType.inventory ||
        variant === VehicleSelectorType.savedConfig
      ) {
        const packageWord = data.pageContent.labels.package.toLowerCase();
        const packageCapitalized =
          packageWord.charAt(0).toUpperCase() + packageWord.slice(1);
        newVehicleCardInfo = {
          ...newVehicleCardInfo,
          colors: [
            vehicleInfo ? parseColorData(vehicleInfo.exteriorColor) : {},
            vehicleInfo ? parseColorData(vehicleInfo.interiorColor) : {},
          ],
          bullets: [
            vehicleInfo?.engine,
            vehicleInfo?.drivetrain,
            ...(vehicleInfo?.packages.map(
              (_package) => `${_package} ${packageCapitalized}`
            ) || []),
          ],
          images: data?.vehicleData?.photos,
        } as ICardInformationComplex;
      } else if (variant === VehicleSelectorType.offers) {
        const offerCardInfo = data?.vehicleData?.offersInfo;
        newVehicleCardInfo = {
          ...newVehicleCardInfo,
          imgAlt: offerCardInfo?.vehicleCardDisclaimer?.description
            ? offerCardInfo?.vehicleCardDisclaimer.description
            : '',
          img: offerCardInfo?.vehicleCardImage,
          description: offerCardInfo?.vehicleCardDisclaimer?.description
            ? offerCardInfo?.vehicleCardDisclaimer.description
            : '',
        } as ICardInformationBasic;
      }
      if (variant === VehicleSelectorType.inventory) {
        newVehicleCardInfo = {
          ...newVehicleCardInfo,
          msrp: {
            value: vehicleInfo?.inventory?.msrp,
            disclaimer: data.pageContent.labels.msrpDisclaimer,
            caption: data.pageContent.labels.msrp,
          },
          captionTitle: vehicleInfo?.inventory?.dealer?.name,
          captionDescription: getTransitLotText(
            vehicleInfo?.inventory?.numberInTransit || 0,
            vehicleInfo?.inventory?.numberOnSite || 0,
            labels
          ),
        } as ICardInformationComplex;
      }
      setVehicleCardInfo(newVehicleCardInfo);
      onEnabled && onEnabled();
      if (selectedModel && selectedPackage) {
        dispatch(
          setVehicleData({
            modelSeries: selectedModel?.taxonomyVehicle || '',
            modelYear: selectedModel?.modelYear || '',
            modelID: selectedPackage.vehicleModel || '',
          })
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPackage]);

  const onModelSelectChange = (value: string) => {
    const newSelectedTaxonomy = selectedTaxonomy.vehicles.find(
      (vehicle) => createIdFromText(vehicle.taxonomyName) === value
    );
    if (newSelectedTaxonomy) {
      trackEvent({
        label: `selector:toggle filter model`,
        category: {
          includePageName: true,
          text: 'selector',
        },
        type: 'selector',
        modelSeries: newSelectedTaxonomy?.taxonomyVehicle || 'no model',
        modelYear: newSelectedTaxonomy?.modelYear || 'no model',
      });

      const key = `${newSelectedTaxonomy.taxonomyVehicle?.toLowerCase()}-${
        newSelectedTaxonomy.modelYear
      }`;
      dispatch(setCurrentConfig(key));

      setSelectedModel(newSelectedTaxonomy);
    }
  };

  const onPackageSelectChange = (value: string) => {
    if (selectedModel?.packages) {
      const newSelectedPackage = selectedModel.packages.find(
        (pkg) => createIdFromText(pkg.packageName) === value
      );
      if (newSelectedPackage) {
        trackEvent({
          label: `selector:toggle filter package`,
          category: {
            includePageName: true,
            text: 'selector',
          },
          type: 'selector',
          modelSeries: selectedModel?.taxonomyVehicle || 'no model',
          modelYear: selectedModel?.modelYear || 'no model',
          modelTrim: cleanHTMLFromString(newSelectedPackage.packageName),
          modelId: newSelectedPackage.vehicleModel,
        });

        if (configuration) {
          dispatch(setConfigStatus(false));
        }
        setSelectedPackage(newSelectedPackage);
      }
    }
  };

  return (
    <Grid container direction="row" className="vehicle-selector">
      <Grid item xs={12} md={6}>
        <h3 className={`${classes.title} acr-caption-6`}>{labels.model}</h3>
        {!isFormDisabled && (
          <Grid
            container
            direction="row"
            className={`${classes.button_container} ${
              isFormDisabled ? 'disabled' : ''
            }`}
          >
            {taxonomies.map((taxonomy) => (
              <div
                key={`vehicle-selector-${taxonomy.taxonomyName}`}
                className={classes.tab_vehicle_year}
              >
                <span className={`divider`}></span>
                <LinkButton
                  key={taxonomy.taxonomyName}
                  onClick={() => {
                    handleTaxonomyClick(taxonomy);
                  }}
                  classNames={{
                    container: `acr-paragraph-8 ${
                      classes.select_vehicle_year
                    } ${
                      selectedTaxonomy.taxonomyName === taxonomy.taxonomyName &&
                      'selected'
                    }`,
                  }}
                  tabIndex={
                    selectedTaxonomy.taxonomyName === taxonomy.taxonomyName
                      ? -1
                      : 0
                  }
                >
                  <span
                    className={`acr-paragraph-8 ${classes.vehicle_year_tab} ${
                      selectedTaxonomy.taxonomyName === taxonomy.taxonomyName &&
                      'selected'
                    }`}
                  >
                    {taxonomy.taxonomyName}
                  </span>
                </LinkButton>
              </div>
            ))}
          </Grid>
        )}
        <FormControl
          className={`${classes.form_control} model-dropdown ${
            isFormDisabled ? 'disabled' : ''
          }`}
        >
          <Dropdown
            classNames={{
              container: `t-model-select`,
              placeholder: 'acr-caption-10',
            }}
            dataAutomation={'model-select'}
            changeFunction={onModelSelectChange}
            selectedValue={createIdFromText(selectedModel?.taxonomyName)}
            disabled={isFormDisabled}
            placeholder={labels.model}
            //TODO: review taxonomies interfaces
            options={selectedTaxonomy?.vehicles?.map((vehicle) =>
              createDropdownItemObj(vehicle.taxonomyName)
            )}
          />
          {showModelError && (
            <TextRender
              className={`acr-paragraph-8 ${classes.error_tag}`}
              text={labels.missingModelError}
            />
          )}
        </FormControl>

        <h3 className={`${classes.title} acr-caption-6`}>{labels.package}</h3>
        {selectedModel?.supplyMessage && (
          <TextRender
            tagName="p"
            className={`acr-paragraph-12 ${classes.supply_message}`}
            text={selectedModel.supplyMessage}
          />
        )}
        <FormControl
          className={`${classes.form_control} package-dropdown ${
            !selectedModel || !selectedModel.packages || isFormDisabled
              ? 'disabled'
              : ''
          }`}
        >
          <Dropdown
            classNames={{
              container: `t-package-select`,
              placeholder: 'acr-caption-10',
            }}
            dataAutomation={'package-select'}
            changeFunction={onPackageSelectChange}
            selectedValue={createIdFromText(selectedPackage?.packageName)}
            placeholder={labels.package}
            disabled={
              isFormDisabled || !selectedModel || !selectedModel.packages
            }
            options={selectedModel?.packages?.map((_package) =>
              createDropdownItemObj(_package.packageName)
            )}
          />
          {showPackageError && (
            <TextRender
              className={`acr-paragraph-8 ${classes.error_tag}`}
              text={labels.missingPackageError}
            />
          )}
        </FormControl>
      </Grid>

      {selectedModel && // Displaying the Vehicle Card(grid) if a vehicle is selected
        vehicleCardInfo && ( // Validating that VehicleCardInfo is not undefined
          <Grid item xs={12} md={6} className={classes.vehicle_card_container}>
            <VehicleCard variant={variant} cardInfo={vehicleCardInfo} />
          </Grid>
        )}
    </Grid>
  );
}
